import axios from 'axios';
import { Message, Lesson, BattlePassReward, UserStats, Achievement, ChatStyle } from '../types';

const greetings = [
  "Hello! I'm here to help you on your growth journey!",
  "Welcome back! How are you today?",
  "Ready to grow together? I'm here to help!"
];

export const initialMessages: Message[] = [
  {
    id: '1',
    content: greetings[Math.floor(Math.random() * greetings.length)],
    sender: 'ai',
    timestamp: new Date(),
  }
];

const removeMarkdown = (text: string): string => {
  return text
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
    .replace(/#{1,6}\s?([^#\n]+)/g, '$1')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1')
    .replace(/```[^`]*```/g, '')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/^\s*[-*+]\s+/gm, '')
    .replace(/^\s*\d+\.\s+/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/\[Thought Process\].*?\n/g, '')
    .replace(/\[.*?\]/g, '')
    .replace(/\n\s*\n/g, '\n')
    .trim();
};

const cleanCBTResponse = (text: string): string => {
  return text
    .replace(/\[Thought Process\].*?\n/g, '')
    .replace(/\[.*?\]/g, '')
    .replace(/\n\s*\n/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();
};

export const generateResponse = async (message: string, chatStyle: ChatStyle = 'cbt'): Promise<string> => {
  try {
    const apiKey = import.meta.env.VITE_DEEPSEEK_API_KEY;

    if (!apiKey) {
      console.error('DeepSeek API key not found in environment variables');
      return "Sorry, I cannot connect to the AI service right now. Please check the API settings.";
    }

    let systemPrompt;
    switch (chatStyle) {
      case 'cbt':
        systemPrompt = `You are a psychological counselor with 8 years of clinical experience, skilled at transforming professional theories into easily understandable conversations. Please use a warm, humanistic approach with these characteristics:

- Keep each response to 1-2 sentences
- First express understanding or observation
- Then ask questions to explore deeper
- Maintain conversation rhythm: response length ≈ 1.2x user input (about 2-3 sentences)
- Theory application strategies:
  1. Anxiety/OCD → CBT's ABC model
  2. Childhood trauma → Psychoanalytic defense mechanisms
  3. Identity issues → Narrative therapy reconstruction
  4. Goal uncertainty → GROW coaching model
  5. Emotional isolation → Person-centered unconditional positive regard`;
        break;
      case 'casual':
        systemPrompt = `You are a friendly AI assistant, conversing in a relaxed and natural way. Keep each response to 1-2 sentences. Use a casual, approachable tone.`;
        break;
      case 'logical':
        systemPrompt = `You are a professional logical analysis expert. Your responses must follow this format and requirements:

1. Section Format:
   - Start each section with [Section N]
   - Each section limited to 100 words
   - Clear logical connections between sections
   - Avoid formatting like bold or italic

2. Content Structure:
   [Section 1] Problem overview and core points
   [Section 2] Deep analysis of root causes
   [Section 3] Concrete solution proposals
   [Section 4] Summary and next steps

3. Expression Requirements:
   - Use clear logical reasoning
   - Provide specific examples
   - Maintain professional objectivity
   - Ensure content flow

4. Response Principles:
   - Deep analysis without excessive academia
   - Actionable recommendations
   - Support points with data when appropriate
   - Anticipate and address potential obstacles`;
        break;
    }

    const response = await axios.post(
      'https://api.deepseek.com/v1/chat/completions',
      {
        model: 'deepseek-chat',
        messages: [
          {
            role: 'system',
            content: systemPrompt
          },
          {
            role: 'user',
            content: message
          }
        ],
        max_tokens: chatStyle === 'logical' ? 2000 : 1000,
        temperature: chatStyle === 'logical' ? 0.3 : chatStyle === 'cbt' ? 0.7 : 0.8
      },
      {
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json'
        }
      }
    );

    let content = response.data.choices[0].message.content;
    
    if (chatStyle === 'logical') {
      content = removeMarkdown(content);
    } else if (chatStyle === 'cbt') {
      content = cleanCBTResponse(content);
    }

    return content;
  } catch (error) {
    console.error('Error calling DeepSeek API:', error);
    return "Sorry, I cannot process your request right now. Please try again later.";
  }
};

export const mockLessons: Lesson[] = [
  {
    id: 'mr1',
    title: 'Understanding Mental Resilience',
    description: 'Learn the fundamentals of mental resilience and how to build it.',
    duration: '20 min',
    category: 'mental_resilience',
    completed: false,
    xpReward: 100,
    progress: 0,
    stars: 0,
    order: 1,
    steps: [
      {
        id: 'mr1-1',
        type: 'content',
        title: 'What is Mental Resilience?',
        content: `Mental resilience is your ability to bounce back after something tough happens.

It's not about being "tough" all the time—it's about recovering and moving forward.

Story Example:
After failing her driving test, Maya felt disappointed. Instead of giving up, she practiced more and tried again. Passing the next time made her proud, and she realized she could handle setbacks.`,
        xpReward: 20
      },
      {
        id: 'mr1-2',
        type: 'quiz',
        title: 'Quick Check',
        content: 'What does mental resilience help you do?',
        quizOptions: [
          'Avoid all problems',
          'Recover after tough times',
          'Never feel sad',
          'Ignore your feelings'
        ],
        correctAnswer: 'Recover after tough times',
        xpReward: 25
      },
      {
        id: 'mr1-3',
        type: 'content',
        title: 'Resilient, Not Invincible',
        content: `You don't need to ignore feelings to be resilient!
Resilient people still feel stress, sadness, or frustration.
They just know how to keep going, even when things are hard.

Story Example:
When his team lost the championship, Jordan was upset and even cried. But he talked with his coach, learned what he could improve, and kept showing up for practice.`,
        xpReward: 20
      },
      {
        id: 'mr1-4',
        type: 'quiz',
        title: 'Understanding Resilience',
        content: 'True or False: Resilient people never feel upset.',
        quizOptions: [
          'True',
          'False'
        ],
        correctAnswer: 'False',
        xpReward: 25
      },
      {
        id: 'mr1-5',
        type: 'content',
        title: 'Why Does Resilience Matter?',
        content: `Life is full of surprises—some good, some not so good.
Resilience helps you:
- Manage stress better
- Stay healthy
- Succeed, even when things don't go as planned

Story Example:
Sara lost her job unexpectedly. At first, she was worried. But by reaching out for advice and updating her skills, she found a new opportunity—and felt stronger for having overcome the challenge.`,
        xpReward: 20
      },
      {
        id: 'mr1-6',
        type: 'content',
        title: 'Key Ingredients of Resilience',
        content: `Resilient people often have:

1. Optimism: They believe things can get better
2. Flexibility: They can adapt when life changes
3. Support: They ask for help when they need it

Story Example:
After moving to a new city, Alex felt lonely. He joined a local club and made friends. When he missed his family, he called them for support and reminded himself that things would improve.`,
        xpReward: 20
      },
      {
        id: 'mr1-7',
        type: 'quiz',
        title: 'Final Quiz',
        content: 'Which is NOT a part of resilience?',
        quizOptions: [
          'Asking for help',
          'Being flexible',
          'Ignoring problems',
          'Staying optimistic'
        ],
        correctAnswer: 'Ignoring problems',
        xpReward: 40
      }
    ]
  },
  {
    id: 'mr2',
    title: 'Stress Management',
    description: 'Master techniques to handle workplace stress effectively.',
    duration: '20 min',
    category: 'mental_resilience',
    completed: false,
    xpReward: 120,
    progress: 0,
    stars: 0,
    order: 2,
    steps: [
      {
        id: 'mr2-1',
        type: 'content',
        title: 'Understanding Stress',
        content: 'Learn to identify different types of stress and their impact on your mental well-being.',
        xpReward: 30
      },
      {
        id: 'mr2-2',
        type: 'content',
        title: 'Stress Response Techniques',
        content: 'Discover practical methods to manage stress in real-time.',
        xpReward: 40
      },
      {
        id: 'mr2-3',
        type: 'quiz',
        title: 'Stress Management Quiz',
        content: 'What is the first step in managing stress effectively?',
        quizOptions: [
          'Ignore it',
          'Identify the source',
          'Take medication',
          'Quit your job'
        ],
        correctAnswer: 'Identify the source',
        xpReward: 50
      }
    ]
  },
  {
    id: 'mr3',
    title: 'Building Resilience',
    description: 'Develop strategies to bounce back from setbacks.',
    duration: '25 min',
    category: 'mental_resilience',
    completed: false,
    xpReward: 150,
    progress: 0,
    stars: 0,
    order: 3,
    steps: [
      {
        id: 'mr3-1',
        type: 'content',
        title: 'Resilience Fundamentals',
        content: 'Understanding the key components of resilience and how to strengthen them.',
        xpReward: 40
      },
      {
        id: 'mr3-2',
        type: 'quiz',
        title: 'Resilience Check',
        content: 'What is the best approach when facing a setback?',
        quizOptions: [
          'Give up immediately',
          'Learn from the experience',
          'Blame others',
          'Ignore it happened'
        ],
        correctAnswer: 'Learn from the experience',
        xpReward: 60
      }
    ]
  },
  {
    id: 'ei1',
    title: 'Understanding Emotions',
    description: 'Learn to identify and understand different emotions.',
    duration: '15 min',
    category: 'emotional_intelligence',
    completed: false,
    xpReward: 100,
    progress: 0,
    stars: 0,
    order: 1,
    steps: [
      {
        id: 'ei1-1',
        type: 'content',
        title: 'Emotional Awareness',
        content: 'Learn to identify and understand different emotional states.',
        xpReward: 30
      },
      {
        id: 'ei1-2',
        type: 'quiz',
        title: 'Emotion Recognition',
        content: 'Which is a primary emotion?',
        quizOptions: [
          'Jealousy',
          'Fear',
          'Guilt',
          'Pride'
        ],
        correctAnswer: 'Fear',
        xpReward: 40
      }
    ]
  },
  {
    id: 'ei2',
    title: 'Emotional Regulation',
    description: 'Master techniques to manage your emotions effectively.',
    duration: '20 min',
    category: 'emotional_intelligence',
    completed: false,
    xpReward: 120,
    progress: 0,
    stars: 0,
    order: 2,
    steps: [
      {
        id: 'ei2-1',
        type: 'content',
        title: 'Regulation Strategies',
        content: 'Learn practical techniques for managing strong emotions.',
        xpReward: 40
      },
      {
        id: 'ei2-2',
        type: 'quiz',
        title: 'Regulation Check',
        content: 'What is the first step in emotional regulation?',
        quizOptions: [
          'React immediately',
          'Suppress the emotion',
          'Recognize the emotion',
          'Ignore the situation'
        ],
        correctAnswer: 'Recognize the emotion',
        xpReward: 50
      }
    ]
  },
  {
    id: 'ei3',
    title: 'Empathy Development',
    description: 'Enhance your ability to understand others\' emotions.',
    duration: '25 min',
    category: 'emotional_intelligence',
    completed: false,
    xpReward: 150,
    progress: 0,
    stars: 0,
    order: 3,
    steps: [
      {
        id: 'ei3-1',
        type: 'content',
        title: 'Understanding Empathy',
        content: 'Learn the different types of empathy and their importance.',
        xpReward: 50
      },
      {
        id: 'ei3-2',
        type: 'quiz',
        title: 'Empathy Check',
        content: 'What is cognitive empathy?',
        quizOptions: [
          'Feeling others\' emotions',
          'Understanding others\' perspectives',
          'Ignoring others\' feelings',
          'Copying others\' behavior'
        ],
        correctAnswer: 'Understanding others\' perspectives',
        xpReward: 60
      }
    ]
  },
  {
    id: 'p1',
    title: 'Time Management Basics',
    description: 'Learn fundamental techniques for managing your time.',
    duration: '15 min',
    category: 'productivity',
    completed: false,
    xpReward: 100,
    progress: 0,
    stars: 0,
    order: 1,
    steps: [
      {
        id: 'p1-1',
        type: 'content',
        title: 'Time Management Principles',
        content: 'Understanding the basics of effective time management.',
        xpReward: 30
      },
      {
        id: 'p1-2',
        type: 'quiz',
        title: 'Time Management Check',
        content: 'What is the Eisenhower Matrix used for?',
        quizOptions: [
          'Managing emails',
          'Prioritizing tasks',
          'Planning meetings',
          'Taking breaks'
        ],
        correctAnswer: 'Prioritizing tasks',
        xpReward: 40
      }
    ]
  },
  {
    id: 'p2',
    title: 'Deep Work Mastery',
    description: 'Master the art of focused, high-value work.',
    duration: '20 min',
    category: 'productivity',
    completed: false,
    xpReward: 120,
    progress: 0,
    stars: 0,
    order: 2,
    steps: [
      {
        id: 'p2-1',
        type: 'content',
        title: 'Deep Work Principles',
        content: 'Learn the fundamentals of deep work and its importance.',
        xpReward: 40
      },
      {
        id: 'p2-2',
        type: 'quiz',
        title: 'Deep Work Check',
        content: 'What is the main benefit of deep work?',
        quizOptions: [
          'More social time',
          'Higher quality output',
          'Less work overall',
          'More meetings'
        ],
        correctAnswer: 'Higher quality output',
        xpReward: 50
      }
    ]
  },
  {
    id: 'p3',
    title: 'Task Management',
    description: 'Learn effective systems for managing tasks and projects.',
    duration: '25 min',
    category: 'productivity',
    completed: false,
    xpReward: 150,
    progress: 0,
    stars: 0,
    order: 3,
    steps: [
      {
        id: 'p3-1',
        type: 'content',
        title: 'Task Management Systems',
        content: 'Explore different approaches to managing tasks effectively.',
        xpReward: 50
      },
      {
        id: 'p3-2',
        type: 'quiz',
        title: 'Task Management Check',
        content: 'What is the two-minute rule?',
        quizOptions: [
          'Take a break every 2 minutes',
          'If it takes less than 2 minutes, do it now',
          'Work for 2 minutes at a time',
          'Check email every 2 minutes'
        ],
        correctAnswer: 'If it takes less than 2 minutes, do it now',
        xpReward: 60
      }
    ]
  },
  {
    id: 'rs1',
    title: 'Communication Fundamentals',
    description: 'Master the basics of effective communication.',
    duration: '15 min',
    category: 'relationship_social',
    completed: false,
    xpReward: 100,
    progress: 0,
    stars: 0,
    order: 1,
    steps: [
      {
        id: 'rs1-1',
        type: 'content',
        title: 'Communication Basics',
        content: 'Learn the fundamental principles of effective communication.',
        xpReward: 30
      },
      {
        id: 'rs1-2',
        type: 'quiz',
        title: 'Communication Check',
        content: 'What is active listening?',
        quizOptions: [
          'Interrupting others',
          'Planning your response while others speak',
          'Fully focusing on the speaker',
          'Taking notes only'
        ],
        correctAnswer: 'Fully focusing on the speaker',
        xpReward: 40
      }
    ]
  },
  {
    id: 'rs2',
    title: 'Building Trust',
    description: 'Learn techniques for building trust in relationships.',
    duration: '20 min',
    category: 'relationship_social',
    completed: false,
    xpReward: 120,
    progress: 0,
    stars: 0,
    order: 2,
    steps: [
      {
        id: 'rs2-1',
        type: 'content',
        title: 'Trust Building',
        content: 'Understanding the key components of building trust.',
        xpReward: 40
      },
      {
        id: 'rs2-2',
        type: 'quiz',
        title: 'Trust Check',
        content: 'What is the most important factor in building trust?',
        quizOptions: [
          'Being perfect',
          'Never making mistakes',
          'Consistency in behavior',
          'Avoiding conflict'
        ],
        correctAnswer: 'Consistency in behavior',
        xpReward: 50
      }
    ]
  },
  {
    id: 'rs3',
    title: 'Conflict Resolution',
    description: 'Master techniques for handling conflicts effectively.',
    duration: '25 min',
    category: 'relationship_social',
    completed: false,
    xpReward: 150,
    progress: 0,
    stars: 0,
    order: 3,
    steps: [
      {
        id: 'rs3-1',
        type: 'content',
        title: 'Conflict Resolution Basics',
        content: 'Learn effective strategies for managing and resolving conflicts.',
        xpReward: 50
      },
      {
        id: 'rs3-2',
        type: 'quiz',
        title: 'Conflict Resolution Check',
        content: 'What is the first step in resolving a conflict?',
        quizOptions: [
          'Win at all costs',
          'Identify the real issue',
          'Prove you\'re right',
          'Avoid the person'
        ],
        correctAnswer: 'Identify the real issue',
        xpReward: 60
      }
    ]
  }
];

export const mockBattlePassRewards: BattlePassReward[] = [
  {
    id: '1',
    title: '100 Coins',
    description: 'Virtual currency to spend on customizations',
    level: 1,
    imageSrc: '',
    claimed: true,
    type: 'coins',
    value: 100,
  },
  {
    id: '2',
    title: 'Exclusive Avatar',
    description: 'Special avatar background',
    level: 2,
    imageSrc: '',
    claimed: true,
    type: 'digital',
  },
  {
    id: '3',
    title: 'Nike Discount',
    description: '15% off your next purchase',
    brand: 'Nike',
    level: 5,
    imageSrc: '',
    claimed: false,
    type: 'physical',
  },
  {
    id: '4',
    title: '250 Coins',
    description: 'Virtual currency to spend on customizations',
    level: 8,
    imageSrc: '',
    claimed: false,
    type: 'coins',
    value: 250,
  },
  {
    id: '5',
    title: 'Premium Theme',
    description: 'Unlock an exclusive app theme',
    level: 10,
    imageSrc: '',
    claimed: false,
    type: 'digital',
  },
  {
    id: '6',
    title: 'Disney+ Trial',
    description: '1-month free subscription',
    brand: 'Disney',
    level: 15,
    imageSrc: '',
    claimed: false,
    type: 'digital',
  },
  {
    id: '7',
    title: '500 Coins',
    description: 'Virtual currency to spend on customizations',
    level: 20,
    imageSrc: '',
    claimed: false,
    type: 'coins',
    value: 500,
  },
  {
    id: '8',
    title: 'PARKnSHOP Gift Card',
    description: '$50 gift card for groceries',
    brand: 'PARKnSHOP',
    level: 25,
    imageSrc: '',
    claimed: false,
    type: 'physical',
    value: 50,
  },
];

export const mockUserStats: UserStats = {
  level: 4,
  xp: 350,
  xpToNextLevel: 500,
  coins: 150,
  streak: 5,
  achievements: [],
  moodHistory: [
    {
      date: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000),
      mood: 'neutral',
    },
    {
      date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
      mood: 'good',
    },
    {
      date: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000),
      mood: 'great',
    },
    {
      date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
      mood: 'good',
    },
    {
      date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
      mood: 'low',
    },
    {
      date: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
      mood: 'neutral',
    },
    {
      date: new Date(),
      mood: 'good',
    },
  ],
};

export const checkAndAwardAchievements = (currentAchievements: Achievement[], action: string) => {
  const achievements = [...currentAchievements];

  switch(action) {
    case 'chat':
      if (!achievements.find(a => a.id === 'first_chat')) {
        achievements.push({
          id: 'first_chat',
          title: 'First Chat',
          description: 'Started your first conversation',
          icon: '',
          progress: 1,
          target: 1,
          completed: true,
          xpReward: 50
        });
      }
      break;

    case 'lesson':
      if (!achievements.find(a => a.id === 'first_lesson')) {
        achievements.push({
          id: 'first_lesson',
          title: 'Learning Pioneer',
          description: 'Completed your first lesson',
          icon: '',
          progress: 1,
          target: 1,
          completed: true,
          xpReward: 100
        });
      }
      break;
  }

  return achievements.length > currentAchievements.length ? achievements : null;
};